import { z } from "zod";
import type { Api } from "../../services/api";
import { hasLoggedInStore } from "../../domains/auth/stores/deferredDeepLinkStore";
import { localStore } from "../../lib/store";
import { gaEventSharedLinkOpened } from "./domains/share.analytics";

/**
 * Which member's link brought a visitor here: the link half of the viral
 * coefficient.
 *
 * Four kinds of link credit a member:
 *
 * - a share link (`<content>?shared=<sharer id>`, built by the api short link,
 *   `router/webapp/private/share-links.ts`);
 * - a playlist claim QR code (`?shared=<owner>&claim=1`), kept apart because a
 *   printed QR handed out at a congress is distribution, not a peer's share;
 * - a shared JuisciGPT conversation (`/gpt/shared/:token`);
 * - a shared poster (`/v/:token`).
 *
 * The last two name only themselves, by the conversation or poster id. Their
 * public page hides the owner on purpose, so the api resolves the owner at
 * signup (`POST /webapp/public/users`) and the browser never learns it. Their
 * landing path holds the share token, a capability, so it is never stored.
 *
 * The credit is read before anything can strip it (`index.tsx` for the query,
 * the shared page itself once it has rendered) and kept on the device
 * until the visitor signs up. First touch wins. A landing older than the
 * attribution window credits nobody.
 *
 * Only a device that has never been signed in records a credit: a member who
 * opens a colleague's link is not a referral. The credit is kept after the
 * account is created, because the completion event comes later (after the
 * room join, or after a failed one is retried), and is cleared on logout so
 * the next account on the device does not inherit it.
 *
 * Known blind spots, so the coefficient read from these events is a lower
 * bound: a visitor who lands in a mobile browser and then signs up in the
 * installed app keeps the credit only through the URL the app opens after the
 * install (`withShareCredit`), which an iOS fingerprint or clipboard miss
 * loses; and a device that has hosted an account (`hasLoggedInStore` survives
 * logout) never records a new credit.
 */

const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

// `users.id` is a 24-hex ObjectId, minted by `mintObjectId()` at every create
// site (`docs/id-strategy.md` §1). It is what every share link carries, never
// the Firebase or SSO `uid`.
const sharerUserIdSchema = z.string().regex(/^[0-9a-f]{24}$/);

const attributionSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.enum(["share_link", "playlist_qr"]),
      sharerUserId: sharerUserIdSchema,
      landingPath: z.string(),
      capturedAt: z.number(),
    }),
    z.object({
      kind: z.literal("gpt_conversation"),
      conversationId: z.string().min(1),
      capturedAt: z.number(),
    }),
    z.object({
      kind: z.literal("visual_abstract"),
      visualAbstractId: z.uuid(),
      capturedAt: z.number(),
    }),
  ])
  .nullable();

type Attribution = z.infer<typeof attributionSchema>;
export type ReferralChannel = NonNullable<Attribution>["kind"];
// `Omit` per union member, so each landing keeps its own id field.
type SharedContentLanding =
  Extract<
    NonNullable<Attribution>,
    { kind: "gpt_conversation" | "visual_abstract" }
  > extends infer Landing
    ? Landing extends { capturedAt: number }
      ? Omit<Landing, "capturedAt">
      : never
    : never;
type SignupReferral =
  Api["post"]["/webapp/public/users"]["input"]["json"]["referral"];

const shareAttributionStore = localStore(
  "share-attribution",
  null,
  attributionSchema
);

function currentAttribution(now: number): Attribution {
  const attribution = shareAttributionStore.get();
  if (attribution === null) return null;
  if (now - attribution.capturedAt <= ATTRIBUTION_WINDOW_MS) return attribution;
  shareAttributionStore.set(null);
  return null;
}

function canRecord(): boolean {
  return !hasLoggedInStore.get() && currentAttribution(Date.now()) === null;
}

/**
 * What a shared page is, read from its path. The paths are the ones a share
 * link resolves to (`share-links.ts`, the share hooks, the profile, company
 * and room share buttons). The room-scoped prefixes come before `/room/`.
 */
const SHARED_CONTENT_PATHS = [
  ["/room/post/", "article"],
  ["/post/", "article"],
  ["/nectar/", "nectar"],
  ["/room/video/story/", "video_story"],
  ["/video/story/", "video_story"],
  ["/room/video/", "video"],
  ["/video/", "video"],
  ["/room/infographic/", "infographic"],
  ["/infographic/", "infographic"],
  ["/room/playlist/", "playlist"],
  ["/playlist/", "playlist"],
  ["/room/", "room"],
  ["/profile/user/", "profile"],
  ["/company/", "company"],
  ["/gpt/shared/", "gpt_conversation"],
  ["/v/", "visual_abstract"],
] as const;

export type SharedContentType =
  (typeof SHARED_CONTENT_PATHS)[number][1] | "other";

function sharedContentType(pathname: string): SharedContentType {
  const match = SHARED_CONTENT_PATHS.find(([prefix]) =>
    pathname.startsWith(prefix)
  );
  return match === undefined ? "other" : match[1];
}

// The router matches a trailing slash too.
const TOKEN_ROUTES = [
  [/^\/gpt\/shared\/[^/]+\/?$/, "gpt_conversation", "/gpt/shared/:token"],
  [/^\/v\/[^/]+\/?$/, "visual_abstract", "/v/:token"],
] as const;

/**
 * The path an event may carry. A token page reports its route pattern: the
 * token is a capability (see the module comment), even when a hand-edited
 * `shared` query also sits on it.
 */
function reportablePath(pathname: string): string {
  // By prefix, not by the route patterns: any path below a token route may
  // hold the token.
  if (pathname.startsWith("/gpt/shared/")) return "/gpt/shared/:token";
  if (pathname.startsWith("/v/")) return "/v/:token";
  return pathname;
}

/** The share link this visit arrived from, or null for any other visit. */
function sharedLinkArrival(
  location: Pick<Location, "search" | "pathname">
):
  | { kind: "share_link" | "playlist_qr"; sharerUserId: string | null }
  | { kind: "gpt_conversation" | "visual_abstract"; sharerUserId: null }
  | null {
  const query = new URLSearchParams(location.search);
  const shared = query.get("shared");
  if (shared !== null) {
    // A hand-edited or truncated link is still an arrival, with no sharer.
    const parsed = sharerUserIdSchema.safeParse(shared);
    return {
      kind: query.get("claim") === "1" ? "playlist_qr" : "share_link",
      sharerUserId: parsed.success ? parsed.data : null,
    };
  }
  const route = TOKEN_ROUTES.find(([pattern]) =>
    pattern.test(location.pathname)
  );
  return route === undefined ? null : { kind: route[1], sharerUserId: null };
}

/**
 * Boot: report a share arrival, and record the sharer of the link this visit
 * landed on.
 *
 * Every arrival emits `shared_link_opened`, a signed-in reader's and a repeat
 * visit's too: it is the top of the share → sign-up funnel, not a credit. Only
 * a `shared` query records a credit here; a shared conversation or poster
 * records its own once rendered (`recordSharedContentLanding`).
 *
 * `readSignedIn` is the Firebase session, which resolves only after the
 * persisted credential has loaded, so the event is pushed after it.
 */
export function captureShareAttribution(
  location: Pick<Location, "search" | "pathname">,
  readSignedIn: () => Promise<boolean>
): void {
  const arrival = sharedLinkArrival(location);
  if (arrival === null) return;
  // Read now: `location` is the live one, and the page may move during the wait.
  const contentType = sharedContentType(location.pathname);
  const landingPath = reportablePath(location.pathname);
  void readSignedIn().then((loggedIn) => {
    gaEventSharedLinkOpened({ ...arrival, contentType, loggedIn, landingPath });
  });
  // A hand-edited or truncated link credits nobody rather than a wrong id.
  if (arrival.sharerUserId === null) return;
  if (!canRecord()) return;
  shareAttributionStore.set({
    kind: arrival.kind,
    sharerUserId: arrival.sharerUserId,
    landingPath: location.pathname,
    capturedAt: Date.now(),
  });
}

/** A shared conversation or poster rendered for a visitor. */
export function recordSharedContentLanding(
  landing: SharedContentLanding
): void {
  if (!canRecord()) return;
  shareAttributionStore.set({ ...landing, capturedAt: Date.now() });
}

/**
 * The properties the sign-up events carry. `referral_channel` is null when no
 * link brought the visitor, so "organic" is a value and not a missing key.
 * `referrer_user_id` is null for a shared conversation or poster too: the
 * browser never knows their owner, the `users.referred_by_user_id` column does.
 */
export function shareAttributionProps(): {
  referral_channel: ReferralChannel | null;
  referrer_user_id: string | null;
  referrer_landing_path: string | null;
} {
  const attribution = currentAttribution(Date.now());
  if (attribution === null)
    return {
      referral_channel: null,
      referrer_user_id: null,
      referrer_landing_path: null,
    };
  switch (attribution.kind) {
    case "share_link":
    case "playlist_qr":
      return {
        referral_channel: attribution.kind,
        referrer_user_id: attribution.sharerUserId,
        referrer_landing_path: attribution.landingPath,
      };
    case "gpt_conversation":
    case "visual_abstract":
      return {
        referral_channel: attribution.kind,
        referrer_user_id: null,
        referrer_landing_path: null,
      };
    default:
      throw new Error(attribution satisfies never);
  }
}

/** The `referral` of the account-creation body (`POST /webapp/public/users`). */
export function signupReferral(): SignupReferral {
  const attribution = currentAttribution(Date.now());
  if (attribution === null) return null;
  switch (attribution.kind) {
    case "share_link":
    case "playlist_qr":
      return {
        channel: attribution.kind,
        referredByUserId: attribution.sharerUserId,
      };
    case "gpt_conversation":
      return {
        channel: attribution.kind,
        conversationId: attribution.conversationId,
      };
    case "visual_abstract":
      return {
        channel: attribution.kind,
        visualAbstractId: attribution.visualAbstractId,
      };
    default:
      throw new Error(attribution satisfies never);
  }
}

/**
 * The URL the install wall hands to the store, with the recorded sharer on it.
 *
 * The installed app starts from an empty storage: the only credit it can read
 * is the one on the URL it opens after the install. `SharedBySheet` has by
 * then removed `shared` from the address bar, so it is put back here — only on
 * the page the credit was recorded on, where the sheet removed it. Any other
 * page would show the reader a sheet naming a sharer of something else.
 *
 * The sharer is the recorded credit (first touch, kept 30 days), which can
 * differ from the one the browser sheet named on a later visit: the app gets
 * the same sharer the sign-up would have credited.
 */
export function withShareCredit(href: string): string {
  const attribution = currentAttribution(Date.now());
  if (attribution === null) return href;
  switch (attribution.kind) {
    case "share_link":
    case "playlist_qr": {
      const url = new URL(href);
      if (url.pathname !== attribution.landingPath) return href;
      url.searchParams.set("shared", attribution.sharerUserId);
      if (attribution.kind === "playlist_qr")
        url.searchParams.set("claim", "1");
      return url.toString();
    }
    // Their own URL (`/gpt/shared/:token`, `/v/:token`) is the credit.
    case "gpt_conversation":
    case "visual_abstract":
      return href;
    default:
      throw new Error(attribution satisfies never);
  }
}

/** Logout: the next account on this device must not inherit the credit. */
export function clearShareAttribution(): void {
  shareAttributionStore.set(null);
}
